import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { detectVoiceCapabilities } from './lib/speech';
import { useVersionCheck } from './lib/useVersionCheck';
import Sidebar from './components/Sidebar';
import FeedbackButton from './components/FeedbackButton';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Collections from './pages/Collections';
import Settings from './pages/Settings';
import SoundLesson from './pages/SoundLesson';
import SoundLessons from './pages/SoundLessons';
import Manual from './pages/Manual';
import Automatic from './pages/Automatic';
import ResetPassword from './pages/ResetPassword';
import Unsubscribe from './pages/Unsubscribe';
import Changelog from './pages/Changelog';
import AdminChangelog from './pages/AdminChangelog';
import Help from './pages/Help';
import AdminHelp from './pages/AdminHelp';
import AdminStats from './pages/AdminStats';
import Onboarding from './pages/Onboarding';
import Legal from './pages/Legal';
import Install from './pages/Install';
import Challenge from './pages/Challenge';
import BlogPost from './pages/BlogPost';
import Blog from './pages/Blog';
const AdminBlog = lazy(() => import('./pages/AdminBlog'));
import CookieBanner, { applyStoredConsent, initialPersistence } from './components/CookieBanner';
import InstallPrompt from './components/InstallPrompt';
import { pageTitle } from './lib/seo';
import '@/App.css';

// The landing page's own title, read from index.html before anything changes it.
const HOME_TITLE = document.title;

// Titles for the routes that do not set their own. Everything but the landing
// page reads "Page topic | Mimique". The blog, blog posts, install and legal
// pages set theirs in the page (lib/seo.js), so they are not listed here.
const ROUTE_TITLES = {
    '/dashboard': 'Dashboard',
    '/collections': 'Phrases',
    '/settings': 'Settings',
    '/manual': 'Manual practice',
    '/practice': 'Manual practice',
    '/automatic': 'Automatic practice',
    '/listen': 'Automatic practice',
    '/onboarding': 'Welcome',
    '/reset-password': 'Reset password',
    '/unsubscribe': 'Unsubscribe',
    '/changelog': 'Status',
    '/help': 'Help guide',
    '/challenge': '30-Day French Speaking & Pronunciation Challenge',
    '/admin': 'Admin',
    '/admin/stats': 'Admin',
    '/admin/changelog': 'Admin changelog',
    '/admin/help': 'Admin help',
    '/admin/blog': 'Admin blog',
};

// Protected Route wrapper. If the user hasn't finished onboarding, funnel them
// there — except when they're already on /onboarding (avoids a redirect loop).
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    if (user.onboarding_complete === false && location.pathname !== '/onboarding') {
        return <Navigate to="/onboarding" replace />;
    }

    return children;
};

// Layout with sidebar for main app pages
const SidebarLayout = ({ children }) => (
    <div className="flex">
        <Sidebar />
        {/* md:ml-60 offsets for desktop sidebar; pb-16 makes room for mobile bottom nav */}
        <div className="flex-1 md:ml-60 pb-16 md:pb-0 min-w-0">{children}</div>
    </div>
);

function AppRoutes() {
    const location = useLocation();
    // Logged out, there is nothing for the help and feedback button to help
    // with: the guide it opens is the app's, and the feedback form posts as a
    // user. It was showing on the landing page to people with no account.
    const { user } = useAuth();
    const hideFeedback = !user || ['/manual', '/automatic', '/practice', '/listen', '/changelog', '/admin/changelog', '/help', '/admin/help', '/admin', '/admin/stats', '/onboarding', '/challenge'].includes(location.pathname) || location.pathname.startsWith('/sounds/')
        || location.pathname.startsWith('/legal/');

    useEffect(() => {
        if (location.pathname === '/') document.title = HOME_TITLE;
        else if (ROUTE_TITLES[location.pathname]) document.title = pageTitle(ROUTE_TITLES[location.pathname]);
    }, [location.pathname]);

    // Prompt a reload when a newer build has been deployed while this tab stayed open.
    useVersionCheck(location.pathname);

    // Scroll back to the top whenever the route changes, otherwise the new page
    // inherits the previous page's scroll position.
    //
    // A hash is the exception. Arriving at /#blog from a post has to land on
    // the blog section, and that section is not in the page yet when this
    // runs: the landing page fetches its posts and renders nothing until they
    // arrive. So the section is waited for, briefly, rather than looked for
    // once and given up on. Any sign of the reader scrolling for themselves
    // calls it off, so the page never yanks itself out from under them.
    useEffect(() => {
        if (!location.hash) {
            window.scrollTo(0, 0);
            return undefined;
        }

        const id = location.hash.slice(1);
        let timer;
        let attempts = 0;
        const stop = () => {
            clearTimeout(timer);
            window.removeEventListener('wheel', stop);
            window.removeEventListener('touchmove', stop);
            window.removeEventListener('keydown', stop);
        };
        const look = () => {
            const target = document.getElementById(id);
            if (target) {
                target.scrollIntoView();
                stop();
                return;
            }
            // Roughly three seconds, which covers a slow first load of the
            // posts, then the page is left where it is.
            if (attempts++ < 30) timer = setTimeout(look, 100);
            else stop();
        };
        window.addEventListener('wheel', stop, { passive: true });
        window.addEventListener('touchmove', stop, { passive: true });
        window.addEventListener('keydown', stop);
        look();
        return stop;
    }, [location.pathname, location.hash]);

    return (
        <>
        <Routes>
            <Route
                path="/"
                element={<Landing />}
            />
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <Dashboard />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/collections"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <Collections />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/sounds"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <SoundLessons />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/sounds/:id"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <SoundLesson />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <SidebarLayout>
                            <Settings />
                        </SidebarLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/manual"
                element={
                    <ProtectedRoute>
                        <Manual />
                    </ProtectedRoute>
                }
            />
            {/* Legacy alias — keep so in-flight sessions / bookmarks don't break */}
            <Route
                path="/practice"
                element={
                    <ProtectedRoute>
                        <Manual />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/automatic"
                element={
                    <ProtectedRoute>
                        <Automatic />
                    </ProtectedRoute>
                }
            />
            {/* Legacy alias — keep so in-flight sessions / bookmarks don't break */}
            <Route
                path="/listen"
                element={
                    <ProtectedRoute>
                        <Automatic />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/onboarding"
                element={
                    <ProtectedRoute>
                        <Onboarding />
                    </ProtectedRoute>
                }
            />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/changelog" element={<ProtectedRoute><Changelog /></ProtectedRoute>} />
            <Route path="/admin/changelog" element={<AdminChangelog />} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            {/* The dashboard is the admin home. /admin/stats was its old
                address and still works, so bookmarks do not break. */}
            <Route path="/admin" element={<AdminStats />} />
            <Route path="/admin/stats" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/help" element={<AdminHelp />} />
            <Route path="/legal/:slug" element={<Legal />} />
            {/* Public, so it can be linked from an email or a post. Putting an
                app on a home screen is something the browser or the reader has
                to do, so this page offers the button where it can and the
                steps where it cannot. */}
            <Route path="/install" element={<Install />} />
            {/* The November challenge sales page. Public, and deliberately
                links nowhere else so people stay on it until they join. */}
            <Route path="/challenge" element={<Challenge />} />
            {/* /admin/blog is the list; /admin/blog/new and /admin/blog/<id>
                are the editor, each post on its own page. */}
            <Route
                path="/admin/blog"
                element={
                    <Suspense fallback={null}>
                        <AdminBlog />
                    </Suspense>
                }
            />
            <Route
                path="/admin/blog/:id"
                element={
                    <Suspense fallback={null}>
                        <AdminBlog />
                    </Suspense>
                }
            />
            {/* Blog posts sit at the root (/why-i-built-mimique and friends)
                because that is the slug format the SEO plan uses. They are
                written in the blog editor rather than in code, so this route
                matches any single path segment and asks the backend whether it
                is a post, sending anything else home. Every real page above is
                a literal path, and the router ranks those higher, so none of
                them can be swallowed by this. */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/:slug" element={<BlogPost />} />
            {/* Posts live at the root, but /blog/<slug> is the obvious guess
                when writing a link by hand, so it resolves and redirects
                rather than dropping the reader on the landing page. */}
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {!hideFeedback && <FeedbackButton />}
        </>
    );
}

function App() {
    useEffect(() => {
        posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
            api_host: process.env.REACT_APP_POSTHOG_HOST,
            defaults: '2026-01-30',
            person_profiles: 'identified_only',
            capture_exceptions: true,
            // Nothing is captured, and no PostHog cookie is written, until the
            // visitor accepts analytics cookies. Memory persistence keeps the
            // device_id out of storage for anyone who hasn't opted in.
            opt_out_capturing_by_default: true,
            persistence: initialPersistence(),
        });
        applyStoredConsent();
    }, []);

    useEffect(() => {
        // Probe each French voice once for onboundary support; results cached.
        detectVoiceCapabilities().catch(() => {});
    }, []);

    return (
        <BrowserRouter>
            <AuthProvider>
                <div className="App">
                    {/* Noise texture overlay */}
                    <div className="noise-texture"></div>
                    <AppRoutes />
                    <CookieBanner />
                    <InstallPrompt />
                    <Toaster position="bottom-right" />
                </div>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
